import * as React from 'react';
import { TilesList, ITilesGridItem } from '@uifabric/experiments/lib/TilesList';
import { ProgressItemTile } from '../itemTile/ProgressItemTile';
import * as ProgressListStyles from './ProgressList.scss';
import { WithItemCache, IItemCacheStateProps } from '../../withItemCache/WithItemCache';
import ProgressStrings from '../ProgressStrings.resx';
import { css } from '@uifabric/utilities/lib/css';
import { ProgressPhase } from '../../../facets/ProgressFacets';
import { childItemsFacet } from '../../../facets/SetFacets';
import { getOverallProgressPhase } from '../../../operations/progress';

/**
 * @public
 */
export interface IProgressListProps {
    itemKeys: string[];
    groupsOnly?: boolean;
}

const MAX_CHILD_ITEMS_GROUPS_ONLY = 3;

interface IProgressItemContent {
    itemKey: string;
    index: number;
}

/**
 * Component which renders a breakdown of tasks within active progress groups.
 * @public
 */
export class ProgressList extends React.Component<IProgressListProps> {
    public render(): JSX.Element {
        const {
            itemKeys,
            groupsOnly
        } = this.props;

        return (
            <WithItemCache>
                { ({ itemCache }: IItemCacheStateProps): JSX.Element => {
                    const items: ITilesGridItem<IProgressItemContent>[] = [];

                    for (const groupItemKey of itemKeys) {
                        const phase = getOverallProgressPhase(itemCache, { itemKey: groupItemKey });

                        if (phase === ProgressPhase.expired) {
                            continue;
                        }

                        const addChildItems = (parentItemKey: string, level: number) => {
                            items.push({
                                key: parentItemKey,
                                content: {
                                    itemKey: parentItemKey,
                                    index: items.length
                                },
                                onRender: level === 0 ? onRenderProgressHeader : onRenderProgressItem
                            });

                            if (groupsOnly && level >= 1) {
                                return;
                            }

                            const childItems = itemCache.demandItemFacet(childItemsFacet, parentItemKey);
                            const childItemKeys = childItems && childItems.itemKeys;

                            if (childItemKeys) {
                                if (!groupsOnly || childItemKeys.length <= MAX_CHILD_ITEMS_GROUPS_ONLY) {
                                    for (const childItemKey of childItemKeys) {
                                        addChildItems(childItemKey, level + 1);
                                    }
                                }
                            }
                        };

                        addChildItems(groupItemKey, 0);
                    }

                    if (items.length > 0) {
                        return (
                            <TilesList
                                className={ ProgressListStyles.root }
                                items={ items }
                                role='presentation'
                            />
                        );
                    }

                    return (
                        <p>{ ProgressStrings.progressNoItemsMessage }</p>
                    );
                } }
            </WithItemCache>
        );
    }
}

/**
 * Renders the header for a progress group.
 */
function onRenderProgressHeader(content: IProgressItemContent): JSX.Element {
    const {
        itemKey
    } = content;

    return (
        <div className={ css(ProgressListStyles.group) }>
            <ProgressItemTile itemKey={ itemKey } format='group' />
        </div>
    );
}

/**
 * Renders a single item within a progress group.
 */
function onRenderProgressItem(content: IProgressItemContent): JSX.Element {
    const {
        itemKey
    } = content;

    return (
        <div className={ css(ProgressListStyles.item) }>
            <ProgressItemTile itemKey={ itemKey } format='item' />
        </div>
    );
}
